import { useEffect, useRef, type RefObject } from "react";
import {
  createPointField,
  type Anchor,
  type PointFieldHandle,
  type RenderFn,
} from "./pointField";

export interface UsePointFieldArgs {
  containerRef: RefObject<HTMLElement | null>;
  canvasRef: RefObject<HTMLCanvasElement | null>;
  frontCanvasRef?: RefObject<HTMLCanvasElement | null>;
  render: RenderFn;
  onAnchors?: (anchors: Anchor[]) => void;
  satMinR?: number;
  resultCount?: number;
  freezeOnHover?: boolean;
  scrub0?: number;
}

export function usePointField(
  args: UsePointFieldArgs,
): RefObject<PointFieldHandle | null> {
  const handleRef = useRef<PointFieldHandle | null>(null);
  const anchorsRef = useRef(args.onAnchors);
  anchorsRef.current = args.onAnchors;

  const {
    containerRef,
    canvasRef,
    frontCanvasRef,
    render,
    satMinR,
    resultCount,
    freezeOnHover,
    scrub0,
  } = args;

  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;

    const handle = createPointField({
      container,
      canvas,
      frontCanvas: frontCanvasRef?.current ?? null,
      render,
      onAnchors: (anchors) => anchorsRef.current?.(anchors),
      satMinR,
      resultCount,
      freezeOnHover,
      scrub0,
    });
    handleRef.current = handle;

    return () => {
      handle.destroy();
      handleRef.current = null;
    };
    // `render` is the only meaningful dependency; the refs are stable and the
    // numeric options are constants at each call site.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [render]);

  return handleRef;
}
